import React, { useState } from "react";
import {
  Megaphone,
  Loader2,
  Facebook,
  Linkedin,
  MessageCircle,
  Copy,
  Check,
  ImageIcon,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const fmtErr = (e, fallback) =>
  e?.response?.data?.detail || e?.message || fallback || "Something went wrong.";

const PLATFORMS = [
  { key: "facebook", label: "Facebook", Icon: Facebook, tint: "text-[#1877F2]" },
  { key: "twitter", label: "X / Twitter", Icon: MessageCircle, tint: "text-white" },
  { key: "linkedin", label: "LinkedIn", Icon: Linkedin, tint: "text-[#0A66C2]" },
  { key: "whatsapp", label: "WhatsApp", Icon: MessageCircle, tint: "text-[#25D366]" },
];

const CopyBox = ({ id, label, Icon, tint, text }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text || "");
      setCopied(true);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy — select and copy manually.");
    }
  };
  return (
    <div className="bg-[#050B14] border border-white/10 rounded-sm p-3" data-testid={`promo-box-${id}`}>
      <div className="flex items-center justify-between mb-2">
        <span className={`inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider ${tint}`}>
          <Icon size={14} /> {label}
        </span>
        <button
          onClick={copy}
          data-testid={`promo-copy-${id}`}
          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-cyan-300 hover:text-white"
        >
          {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <p className="text-sm text-slate-200 whitespace-pre-wrap">{text || "—"}</p>
    </div>
  );
};

const PromoPack = () => {
  const [loading, setLoading] = useState(false);
  const [pack, setPack] = useState(null);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/promo/generate", {});
      setPack(data);
      if (!data.count) toast.error("No eligible articles found right now.");
      else toast.success(`Generated ${data.count} promo${data.count > 1 ? "s" : ""}.`);
    } catch (e) {
      toast.error(fmtErr(e, "Couldn't generate the promo pack."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="promo-panel">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 bg-[#0A192F] border border-white/10 rounded-sm">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-300 flex items-center gap-2">
            <Megaphone size={12} /> Daily Promo Pack
          </p>
          <h2 className="font-heading text-2xl font-bold text-white mt-1">Ready-to-paste posts.</h2>
          <p className="text-sm text-slate-400 mt-1 max-w-xl">
            Turns the two newest Paisley stories into Facebook, X, LinkedIn and WhatsApp copy —
            British English, on-brand, with an image suggestion. For the channels Auto-Pilot cannot post for you.
          </p>
        </div>
        <button
          onClick={generate}
          disabled={loading}
          data-testid="promo-generate-btn"
          className="shrink-0 inline-flex items-center gap-2 px-5 py-3 bg-[#00F0FF] text-[#050B14] font-bold text-xs uppercase tracking-widest rounded-sm hover:bg-[#33F3FF] disabled:opacity-50"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Megaphone size={14} />}
          {loading ? "Writing…" : "Generate today's pack"}
        </button>
      </div>

      {loading && !pack && (
        <div className="flex items-center justify-center py-16 text-slate-400" data-testid="promo-loading">
          <Loader2 className="animate-spin mr-2" /> Drafting your promo pack…
        </div>
      )}

      {pack?.items?.map((item, idx) => (
        <div key={idx} className="p-5 bg-[#0A192F] border border-white/10 rounded-sm" data-testid={`promo-item-${idx}`}>
          <div className="flex items-start gap-4 mb-4">
            {item.image_url && (
              <img
                src={item.image_url}
                alt={item.title}
                className="w-28 h-20 object-cover rounded-sm border border-white/10 shrink-0"
              />
            )}
            <div className="min-w-0">
              <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-cyan-300">{item.source}</p>
              <h3 className="text-white font-semibold">{item.title}</h3>
              <a
                href={item.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs text-cyan-400 hover:text-white mt-1"
              >
                <ExternalLink size={12} /> {item.url}
              </a>
              {item.variants?.image_suggestion && (
                <p className="text-xs text-slate-400 mt-2 flex items-start gap-1.5">
                  <ImageIcon size={13} className="mt-0.5 shrink-0" /> {item.variants.image_suggestion}
                </p>
              )}
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {PLATFORMS.map(({ key, label, Icon, tint }) => (
              <CopyBox
                key={key}
                id={`${idx}-${key}`}
                label={label}
                Icon={Icon}
                tint={tint}
                text={item.variants?.[key]}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default PromoPack;
